import {
  poolCardPalette,
  poolCardSportPath,
  poolCardType,
  POOL_CARD_TYPE_PATHS,
} from "@sportsmet/theme/poolCards";
import { useState, type CSSProperties, type ReactNode } from "react";
import "./PoolCardContent.css";

export function poolCardStyle(contestType: string): CSSProperties {
  const p = poolCardPalette(contestType);
  return {
    "--pool-fill": p.fill,
    "--pool-on": p.on,
    "--pool-icon-light": p.light,
    "--pool-icon-dark": p.dark,
  } as CSSProperties;
}
function Glyph({
  path,
  size = 16,
}: {
  path: string;
  size?: number;
}): ReactNode {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={path} />
    </svg>
  );
}
export type typePoolCardContentProps = {
  name: string;
  heading?: boolean;
  contestType: string;
  sport?: string | undefined;
  facts: readonly string[];
  date?: string | undefined;
  byline?: string | null | undefined;
  imageUrl?: string | null | undefined;
  children?: ReactNode;
};
/** Shared body for joined, invited, search and account cards; no permissions or API calls. */
export function PoolCardContent(props: typePoolCardContentProps): ReactNode {
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const sportPath = poolCardSportPath(props.sport ?? "");
  const typePath = POOL_CARD_TYPE_PATHS[poolCardType(props.contestType)];
  return (
    <div className="pool-card-content" style={poolCardStyle(props.contestType)}>
      <div className="pool-card-art" aria-hidden="true">
        {sportPath ? (
          <Glyph path={sportPath} size={56} />
        ) : (
          <span className="text-caption">Pool</span>
        )}
        {props.imageUrl && failedImage !== props.imageUrl ? (
          <img
            src={props.imageUrl}
            alt=""
            onError={() => {
              setFailedImage(props.imageUrl ?? null);
            }}
          />
        ) : null}
        <span className="pool-card-badge">
          <Glyph path={typePath} size={20} />
        </span>
      </div>
      <div className="pool-card-copy">
        <span
          className="pool-card-title text-ink"
          role={props.heading ? "heading" : undefined}
          aria-level={props.heading ? 3 : undefined}
        >
          {props.name}
        </span>
        <div className="pool-card-facts text-caption text-ink-secondary">
          {[props.date, ...props.facts].filter(Boolean).map((fact, i) => (
            <span className="pool-card-fact" key={`${i}-${fact}`}>
              <Glyph
                path={
                  props.date && i === 0
                    ? "M4 5h16v16H4z M4 10h16 M8 3v4 M16 3v4"
                    : i === (props.date ? 1 : 0)
                      ? (sportPath ?? typePath)
                      : typePath
                }
              />
              <span>{fact}</span>
            </span>
          ))}
        </div>
        {props.byline ? (
          <span className="text-caption text-ink-secondary">
            {props.byline}
          </span>
        ) : null}
        {props.children}
      </div>
    </div>
  );
}
